import React from 'react';
import * as stylex from '@stylexjs/stylex';
import type { StyleXStyles } from '@stylexjs/stylex';
import { mergeProps, useButton, useLink, useHover, useFocusRing, type AriaButtonOptions } from 'react-aria';
import type { LinkButtonProps } from 'react-aria/useButton';
import type { Button_routerOptions as RouterOptions } from '../../externals/ReactAria.gen';
import '../Consts.stylex';
import { Transitions } from '../Consts.stylex';
import './Button.stylex';
import { CssVars } from './Button.stylex';
import '../theme/Colors.stylex';
import { ColorNeutral, ColorBrand, ColorSuccess, ColorDanger, ColorWarning, ColorInformation, ColorNeutralInverse } from '../theme/Colors.stylex';
import '../theme/Radius.stylex';
import { CornerRadius } from '../theme/Radius.stylex';
import '../theme/Spaces.stylex';
import { Padding, Gap } from '../theme/Spaces.stylex';
import '../theme/Fonts.stylex';
import { FontFamily, FontSize, FontWeight } from '../theme/Fonts.stylex';
import { Icon, type IconName } from '../images-and-icons/Icon';
import { Spinner } from '../feedback-indicators/Spinner';
import { Tooltip, TooltipSpan } from '../overlays/Tooltip';
import { getJwt, isImpersonated } from '../../bundles/Auth/Auth_Jwt';
const isCurrentSessionImpersonated = (): boolean => {
  const jwt = getJwt();
  return jwt ? isImpersonated(jwt) : false;
};
const isSameOriginHref = (href?: string): boolean => {
  if (!href) {
    return false;
  }
  if (typeof window === 'undefined') {
    return !/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(href) && !href.startsWith('//');
  }
  try {
    return new URL(href, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
};

// MARK: exported types
export type Size = 'small' | 'medium' | 'legacy_large' | 'large' | 'xlarge';
export type Variant = 'solid' | {
  NAME: 'ghost';
  VAL: {
    bordered?: boolean;
  };
} | 'outline' | 'subtle';
export type Tone = {
  NAME: 'neutral';
  VAL: {
    accent?: boolean;
    quiet?: boolean;
  };
} | 'brand' | 'success' | 'danger' | 'warning' | 'information';

// MARK: root
// ==========
type VariantName = 'solid' | 'ghost' | 'outline' | 'subtle';
type State = 'default' | 'hovered' | 'pressed' | 'disabled';
type Shape = 'rounded' | 'pill';
const variantName = (variant: Variant): VariantName => typeof variant === 'object' ? variant.NAME : variant;
const isWindowsOS = typeof navigator !== 'undefined' && navigator.userAgent.includes('Windows');

// NOTE - Similar to `usePress` from React Aria, but doesn't stop event propagation
// > Useful for decorative elements that need to let the parent handle the actual press.
const useStatusLightAnimation = (statusLight: boolean) => {
  const previousStatusLight = React.useRef(statusLight);
  const [animated, setAnimated] = React.useState(false);
  React.useEffect(() => {
    if (statusLight && !previousStatusLight.current) {
      setAnimated(true);
    }
    previousStatusLight.current = statusLight;
  }, [statusLight]);
  return animated;
};
const useCustomPress = ({
  isDisabled
}: {
  isDisabled?: boolean;
}) => {
  const [isPressed, setIsPressed] = React.useState(false);
  const pressProps = !isDisabled ? {
    onPointerDown: () => setIsPressed(true),
    onPointerUp: () => setIsPressed(false),
    onPointerLeave: () => setIsPressed(false)
  } : {};
  return {
    pressProps,
    isPressed
  };
};
type ButtonProps = LinkButtonProps & {
  ariaProps?: AriaButtonOptions<'button'>;
  ariaLabel?: string;
  label?: string;
  variant?: Variant;
  tone?: Tone;
  size?: Size;
  shape?: Shape;
  bold?: boolean;
  active?: boolean;
  pending?: boolean;
  disabled?: boolean;
  inert?: boolean;
  grow?: boolean;
  noShrink?: boolean;
  icon?: IconName;
  iconSize?: number;
  trailingIcon?: IconName;
  endElement?: React.ReactNode;
  badgeElement?: React.ReactNode;
  tooltipText?: string;
  statusLight?: boolean;
  excludeFromTabOrder?: boolean;
  stylex?: StyleXStyles;
  routerOptions?: RouterOptions;
  // NOTE - extra DOM attributes (e.g. ARIA attributes computed by a React Aria collection hook)
  domProps?: React.HTMLAttributes<HTMLElement>;
  onPress?: () => void;
};
function ButtonComponent({
  ariaProps,
  ariaLabel,
  label,
  variant = 'outline',
  tone = {
    NAME: 'neutral',
    VAL: {}
  },
  size = 'medium',
  shape = 'rounded',
  bold: derivedBold,
  active: derivedActive = false,
  disabled = false,
  inert = false,
  pending = false,
  grow = false,
  noShrink = false,
  icon,
  iconSize,
  trailingIcon,
  endElement,
  badgeElement,
  excludeFromTabOrder,
  tooltipText,
  statusLight = false,
  stylex: derivedStyle,
  href,
  target,
  routerOptions,
  domProps,
  onPress
}: ButtonProps, ref: React.ForwardedRef<HTMLButtonElement>) {
  const isDecorativeButton = !href && !onPress && ariaProps?.onPress === undefined;

  // NOTE - When impersonating, JWT is stored in sessionStorage which is not shared
  // across tabs. Disable target="_blank" for same-origin links to preserve the session.
  const effectiveTarget = target === '_blank' && isSameOriginHref(href) && isCurrentSessionImpersonated() ? '_self' : target;
  const props = mergeProps({
    'aria-label': ariaLabel || label || icon as string || 'button',
    'aria-disabled': disabled || inert || pending ? true : undefined,
    isDisabled: disabled || inert || pending,
    elementType: href ? 'a' : isDecorativeButton ? 'span' : 'button',
    excludeFromTabOrder,
    href,
    target: effectiveTarget,
    routerOptions,
    onPress
  }, ariaProps);
  const {
    buttonProps,
    isPressed: pressedButton
  } = useButton(props, ref as React.RefObject<HTMLButtonElement>);
  const {
    linkProps,
    isPressed: pressedLink
  } = useLink(props, ref as React.RefObject<HTMLButtonElement>);
  const {
    hoverProps,
    isHovered
  } = useHover({
    isDisabled: props.isDisabled
  });
  const {
    pressProps: pressProps,
    isPressed: customPressedButton
  } = useCustomPress({
    isDisabled: props.isDisabled
  });
  const {
    focusProps,
    isFocusVisible: keyboardFocused
  } = useFocusRing();
  const animatedStatusLight = useStatusLightAnimation(statusLight);

  // NOTE - The spinner is heavy (~25 svg nodes), it's mounted only once the button
  // has been pending, then kept so that its exit transition can play.
  const [spinnerMounted, setSpinnerMounted] = React.useState(pending);
  if (pending && !spinnerMounted) setSpinnerMounted(true);
  const hovered = isHovered || keyboardFocused;
  const active = derivedActive || pending || hovered;
  const pressed = isDecorativeButton ? customPressedButton : pressedButton || pressedLink;
  const isIconButton = !label && !trailingIcon && !endElement && !badgeElement;
  const pressedContent = pressed && !trailingIcon;
  const bold = derivedBold === undefined ? !isIconButton : derivedBold;
  const iconDimensions = iconSize !== undefined ? {
    width: iconSize,
    height: iconSize
  } : iconDimensionsFromSize(size, variant, isIconButton);
  const iconColor = iconColorFromParams(tone, variant, disabled, active, pressed);
  const ElementType: React.ElementType = props.elementType;
  const interactionProps = isDecorativeButton ? mergeProps(pressProps, hoverProps, focusProps) : mergeProps(href ? linkProps : buttonProps, hoverProps, focusProps);
  const inertProp = inert ? {
    inert: ''
  } as Record<string, unknown> : {};
  let component = <ElementType ref={ref} {...inertProp} {...interactionProps} {...domProps} {...styleProps(disabled, pending, active, pressed, keyboardFocused, size, shape, tone, bold, variant, grow, noShrink, isIconButton, derivedStyle)}>
      {icon && <div {...iconWrapperStyleProps()}>
          <Icon bold={bold} name={icon} fill={iconColor} width={iconDimensions.width} height={iconDimensions.height} pressed={isIconButton && pressedContent} />
          {statusLight && <div {...statusLightStyleProps(iconDimensions.width, animatedStatusLight)} />}
        </div>}
      {label && <span {...labelStyleProps(pressedContent)}>{label}</span>}
      <div aria-hidden={!pending} {...spinnerProps(pending, isIconButton, iconDimensions.width, tone, variant, active, pressed)}>
        {spinnerMounted && <Spinner size={iconDimensions.width} color={iconColor} inert={!pending} />}
      </div>
      {endElement}
      {trailingIcon && <Icon bold={bold} name={trailingIcon} fill={iconColor} width={iconDimensions.width} height={iconDimensions.height} />}
      {badgeElement}
    </ElementType>;
  return tooltipText ? <Tooltip content={<TooltipSpan text={tooltipText} />} placement="top" arrowed={false} offset={3} delay={inert ? 0 : 350} closeDelay={0}>
      {component}
    </Tooltip> : component;
}
const ForwardedButtonComponent = React.forwardRef(ButtonComponent);
const MemoizedButtonComponent = React.memo(ForwardedButtonComponent) as typeof ForwardedButtonComponent;
export { MemoizedButtonComponent as Button, type ButtonProps };

// MARK: style:size
const sizeStyles = {
  small: {
    "height-kZKoxP": "height-x10w6t97",
    "--iconButton_width-x3uk9vh": "--iconButton_width-x3uk9vh-xuv97js",
    "minWidth-k7Eaqz": "minWidth-xxsxjwm",
    "padding-kmVPX3": "padding-x17ihyg6",
    "--spinner_right-x4kt2lm": "--spinner_right-x4kt2lm-x1aqldh7",
    "gap-kOIVth": "gap-xgolt85",
    "fontSize-kGuDYH": "fontSize-x106xqen",
    $$css: "wino-gc:src/resources/actions/Button.tsx:339"
  },
  medium: {
    "height-kZKoxP": "height-x1vqgdyp",
    "--iconButton_width-x3uk9vh": "--iconButton_width-x3uk9vh-x9n9st5",
    "minWidth-k7Eaqz": "minWidth-x450l9j",
    "padding-kmVPX3": "padding-xb4tss4",
    "--spinner_right-x4kt2lm": "--spinner_right-x4kt2lm-xxlafi0",
    "gap-kOIVth": "gap-xt0mmm2",
    "fontSize-kGuDYH": "fontSize-x1egw0ox",
    $$css: "wino-gc:src/resources/actions/Button.tsx:348"
  },
  legacy_large: {
    "height-kZKoxP": "height-x5kalc8",
    "--iconButton_width-x3uk9vh": "--iconButton_width-x3uk9vh-x1u1hamb",
    "minWidth-k7Eaqz": "minWidth-xktpd3l",
    "padding-kmVPX3": "padding-xdeice",
    "--spinner_right-x4kt2lm": "--spinner_right-x4kt2lm-xn430pc",
    "gap-kOIVth": "gap-xqcaodp",
    "fontSize-kGuDYH": "fontSize-x1vcisby",
    $$css: "wino-gc:src/resources/actions/Button.tsx:357"
  },
  large: {
    "height-kZKoxP": "height-xsdox4t",
    "--iconButton_width-x3uk9vh": "--iconButton_width-x3uk9vh-x1lem8q6",
    "minWidth-k7Eaqz": "minWidth-xktpd3l",
    "padding-kmVPX3": "padding-xdeice",
    "--spinner_right-x4kt2lm": "--spinner_right-x4kt2lm-xn430pc",
    "gap-kOIVth": "gap-xqcaodp",
    "fontSize-kGuDYH": "fontSize-x1vcisby",
    $$css: "wino-gc:src/resources/actions/Button.tsx:366"
  },
  xlarge: {
    "height-kZKoxP": "height-xng8ra",
    "--iconButton_width-x3uk9vh": "--iconButton_width-x3uk9vh-x1osyfan",
    "minWidth-k7Eaqz": "minWidth-x1i9suas",
    "padding-kmVPX3": "padding-xzp7fdy",
    "--spinner_right-x4kt2lm": "--spinner_right-x4kt2lm-x1bovcwr",
    "gap-kOIVth": "gap-x2u8dy9",
    "fontSize-kGuDYH": "fontSize-xv1t9fe",
    $$css: "wino-gc:src/resources/actions/Button.tsx:375"
  }
};

// MARK: style:Neutral
const neutralSolidStyles = {
  default: {
    $$css: "wino-gc:src/resources/actions/Button.tsx:388"
  },
  hovered: {
    "backgroundColor-kWkggS": "backgroundColor-xv6aotp",
    "color-kMwMTN": "color-x1rfp1va",
    "outlineColor-kjBf7l": "outlineColor-x1rcsaz8",
    $$css: "wino-gc:src/resources/actions/Button.tsx:389"
  },
  pressed: {
    "backgroundColor-kWkggS": "backgroundColor-xug7tn3",
    "color-kMwMTN": "color-x1rfp1va",
    "outlineColor-kjBf7l": "outlineColor-x1rcsaz8",
    $$css: "wino-gc:src/resources/actions/Button.tsx:394"
  },
  disabled: {
    "backgroundColor-kWkggS": "backgroundColor-x1sp21dj",
    "color-kMwMTN": "color-xyemlsm",
    $$css: "wino-gc:src/resources/actions/Button.tsx:399"
  }
};
const neutralGhostStyles = {
  default: {
    "backgroundColor-kWkggS": "backgroundColor-x9m8vy9",
    "borderColor-kVAM5u": "borderColor-x8u4suw",
    "color-kMwMTN": "color-x1w1nkkj",
    $$css: "wino-gc:src/resources/actions/Button.tsx:405"
  },
  hovered: {
    "backgroundColor-kWkggS": "backgroundColor-x1y0x0bx",
    "borderColor-kVAM5u": "borderColor-x8u4suw",
    "color-kMwMTN": "color-xsqyr71",
    "outlineColor-kjBf7l": "outlineColor-x1rcsaz8",
    $$css: "wino-gc:src/resources/actions/Button.tsx:410"
  },
  pressed: {
    "backgroundColor-kWkggS": "backgroundColor-x87y9mw",
    "borderColor-kVAM5u": "borderColor-x8u4suw",
    "color-kMwMTN": "color-xsqyr71",
    "outlineColor-kjBf7l": "outlineColor-x1rcsaz8",
    $$css: "wino-gc:src/resources/actions/Button.tsx:416"
  },
  disabled: {
    "backgroundColor-kWkggS": "backgroundColor-x1sp21dj",
    "borderColor-kVAM5u": "borderColor-xv9nw99",
    "color-kMwMTN": "color-xyemlsm",
    $$css: "wino-gc:src/resources/actions/Button.tsx:422"
  }
};
const neutralGhostWithAccentStyles = {
  default: {
    "backgroundColor-kWkggS": "backgroundColor-x1xoy2ja",
    "borderColor-kVAM5u": "borderColor-x5bw2fz",
    "color-kMwMTN": "color-x1w1nkkj",
    $$css: "wino-gc:src/resources/actions/Button.tsx:429"
  },
  hovered: {
    "backgroundColor-kWkggS": "backgroundColor-xxi5u5r",
    "borderColor-kVAM5u": "borderColor-x5bw2fz",
    "color-kMwMTN": "color-xsqyr71",
    "outlineColor-kjBf7l": "outlineColor-x1rcsaz8",
    $$css: "wino-gc:src/resources/actions/Button.tsx:434"
  },
  pressed: {
    "backgroundColor-kWkggS": "backgroundColor-x87y9mw",
    "borderColor-kVAM5u": "borderColor-x5bw2fz",
    "color-kMwMTN": "color-xsqyr71",
    "outlineColor-kjBf7l": "outlineColor-x1rcsaz8",
    $$css: "wino-gc:src/resources/actions/Button.tsx:440"
  },
  disabled: {
    "backgroundColor-kWkggS": "backgroundColor-x1sp21dj",
    "borderColor-kVAM5u": "borderColor-xv9nw99",
    "color-kMwMTN": "color-xyemlsm",
    $$css: "wino-gc:src/resources/actions/Button.tsx:446"
  }
};
const neutralOutlineOrSubtleStyles = {
  default: {
    "backgroundColor-kWkggS": "backgroundColor-x6isuab",
    "borderColor-kVAM5u": "borderColor-x8u4suw",
    "color-kMwMTN": "color-x1w1nkkj",
    $$css: "wino-gc:src/resources/actions/Button.tsx:453"
  },
  hovered: {
    "backgroundColor-kWkggS": "backgroundColor-x9m8vy9",
    "borderColor-kVAM5u": "borderColor-x1lq5qq1",
    "color-kMwMTN": "color-xsqyr71",
    "outlineColor-kjBf7l": "outlineColor-x1rcsaz8",
    $$css: "wino-gc:src/resources/actions/Button.tsx:458"
  },
  pressed: {
    "backgroundColor-kWkggS": "backgroundColor-xct4vau",
    "borderColor-kVAM5u": "borderColor-x1c9wgzy",
    "color-kMwMTN": "color-x1w1nkkj",
    "outlineColor-kjBf7l": "outlineColor-x1rcsaz8",
    $$css: "wino-gc:src/resources/actions/Button.tsx:464"
  },
  disabled: {
    "backgroundColor-kWkggS": "backgroundColor-x6isuab",
    "borderColor-kVAM5u": "borderColor-xv9nw99",
    "color-kMwMTN": "color-xyemlsm",
    $$css: "wino-gc:src/resources/actions/Button.tsx:470"
  }
};
const neutralOutlineOrSubtleWithAccentStyles = {
  default: {
    "backgroundColor-kWkggS": "backgroundColor-x6isuab",
    "borderColor-kVAM5u": "borderColor-x5bw2fz",
    "color-kMwMTN": "color-x1w1nkkj",
    $$css: "wino-gc:src/resources/actions/Button.tsx:477"
  },
  hovered: {
    "backgroundColor-kWkggS": "backgroundColor-x1xoy2ja",
    "borderColor-kVAM5u": "borderColor-x1lq5qq1",
    "color-kMwMTN": "color-xsqyr71",
    "outlineColor-kjBf7l": "outlineColor-x1rcsaz8",
    $$css: "wino-gc:src/resources/actions/Button.tsx:482"
  },
  pressed: {
    "backgroundColor-kWkggS": "backgroundColor-xct4vau",
    "borderColor-kVAM5u": "borderColor-x1c9wgzy",
    "color-kMwMTN": "color-x1w1nkkj",
    "outlineColor-kjBf7l": "outlineColor-x1rcsaz8",
    $$css: "wino-gc:src/resources/actions/Button.tsx:488"
  },
  disabled: {
    "backgroundColor-kWkggS": "backgroundColor-x6isuab",
    "borderColor-kVAM5u": "borderColor-xv9nw99",
    "color-kMwMTN": "color-xyemlsm",
    $$css: "wino-gc:src/resources/actions/Button.tsx:494"
  }
};
// NOTE - exceptions for {quiet: true}
const neutralDefaultQuietStyles = {
  solid: {
    "backgroundColor-kWkggS": "backgroundColor-xug7tn3",
    "color-kMwMTN": "color-x1rfp1va",
    "outlineColor-kjBf7l": "outlineColor-x1rcsaz8",
    $$css: "wino-gc:src/resources/actions/Button.tsx:502"
  },
  ghost: {
    "backgroundColor-kWkggS": "backgroundColor-x9m8vy9",
    "color-kMwMTN": "color-xt4ixol",
    "outlineColor-kjBf7l": "outlineColor-x1rcsaz8",
    $$css: "wino-gc:src/resources/actions/Button.tsx:507"
  },
  outline: {
    "backgroundColor-kWkggS": "backgroundColor-x12peec7",
    "borderColor-kVAM5u": "borderColor-x8u4suw",
    "color-kMwMTN": "color-xt4ixol",
    "outlineColor-kjBf7l": "outlineColor-x1rcsaz8",
    $$css: "wino-gc:src/resources/actions/Button.tsx:512"
  },
  subtle: {
    "backgroundColor-kWkggS": "backgroundColor-xjbqb8w",
    "color-kMwMTN": "color-xt4ixol",
    "outlineColor-kjBf7l": "outlineColor-x1rcsaz8",
    $$css: "wino-gc:src/resources/actions/Button.tsx:518"
  }
};
const neutralDefaultQuietWithAccentStyles = {
  solid: {
    $$css: "wino-gc:src/resources/actions/Button.tsx:525"
  },
  ghost: {
    "backgroundColor-kWkggS": "backgroundColor-x1xoy2ja",
    "color-kMwMTN": "color-xt4ixol",
    "outlineColor-kjBf7l": "outlineColor-x1rcsaz8",
    $$css: "wino-gc:src/resources/actions/Button.tsx:526"
  },
  outline: {
    "backgroundColor-kWkggS": "backgroundColor-x12peec7",
    "borderColor-kVAM5u": "borderColor-x5bw2fz",
    "color-kMwMTN": "color-xt4ixol",
    "outlineColor-kjBf7l": "outlineColor-x1rcsaz8",
    $$css: "wino-gc:src/resources/actions/Button.tsx:531"
  },
  subtle: {
    "backgroundColor-kWkggS": "backgroundColor-xjbqb8w",
    "color-kMwMTN": "color-xt4ixol",
    "outlineColor-kjBf7l": "outlineColor-x1rcsaz8",
    $$css: "wino-gc:src/resources/actions/Button.tsx:537"
  }
};
// MARK: style:Brand
const brandSolidStyles = {
  default: {
    "backgroundColor-kWkggS": "backgroundColor-x1ryq1pb",
    "color-kMwMTN": "color-x1rfp1va",
    $$css: "wino-gc:src/resources/actions/Button.tsx:545"
  },
  hovered: {
    "backgroundColor-kWkggS": "backgroundColor-x1bqw7n2",
    "color-kMwMTN": "color-x1rfp1va",
    "outlineColor-kjBf7l": "outlineColor-x1pqgiyg",
    $$css: "wino-gc:src/resources/actions/Button.tsx:549"
  },
  pressed: {
    "backgroundColor-kWkggS": "backgroundColor-xtsy7rd",
    "color-kMwMTN": "color-x1rfp1va",
    "outlineColor-kjBf7l": "outlineColor-x1pqgiyg",
    $$css: "wino-gc:src/resources/actions/Button.tsx:554"
  },
  disabled: {
    "backgroundColor-kWkggS": "backgroundColor-x1bqnvm3",
    "color-kMwMTN": "color-xmx9m6c",
    $$css: "wino-gc:src/resources/actions/Button.tsx:559"
  }
};
const brandGhostStyles = {
  default: {
    "backgroundColor-kWkggS": "backgroundColor-x1s5ep7y",
    "borderColor-kVAM5u": "borderColor-x3cyibe",
    "color-kMwMTN": "color-x1mzc8w9",
    $$css: "wino-gc:src/resources/actions/Button.tsx:565"
  },
  hovered: {
    "backgroundColor-kWkggS": "backgroundColor-x1t13sad",
    "borderColor-kVAM5u": "borderColor-x3cyibe",
    "color-kMwMTN": "color-x16f91ol",
    "outlineColor-kjBf7l": "outlineColor-x1pqgiyg",
    $$css: "wino-gc:src/resources/actions/Button.tsx:570"
  },
  pressed: {
    "backgroundColor-kWkggS": "backgroundColor-x1ybj0jp",
    "borderColor-kVAM5u": "borderColor-x3cyibe",
    "color-kMwMTN": "color-x1pzenjb",
    "outlineColor-kjBf7l": "outlineColor-x1pqgiyg",
    $$css: "wino-gc:src/resources/actions/Button.tsx:576"
  },
  disabled: {
    "backgroundColor-kWkggS": "backgroundColor-x1bqnvm3",
    "borderColor-kVAM5u": "borderColor-x1gxkqr0",
    "color-kMwMTN": "color-xmx9m6c",
    $$css: "wino-gc:src/resources/actions/Button.tsx:582"
  }
};
const brandOutlineOrSubtleStyles = {
  default: {
    "backgroundColor-kWkggS": "backgroundColor-x6isuab",
    "borderColor-kVAM5u": "borderColor-x19mpen5",
    "color-kMwMTN": "color-x1mzc8w9",
    $$css: "wino-gc:src/resources/actions/Button.tsx:589"
  },
  hovered: {
    "backgroundColor-kWkggS": "backgroundColor-x1s5ep7y",
    "borderColor-kVAM5u": "borderColor-x8qcncr",
    "color-kMwMTN": "color-x16f91ol",
    "outlineColor-kjBf7l": "outlineColor-x1pqgiyg",
    $$css: "wino-gc:src/resources/actions/Button.tsx:594"
  },
  pressed: {
    "backgroundColor-kWkggS": "backgroundColor-x1ybj0jp",
    "borderColor-kVAM5u": "borderColor-x8qcncr",
    "color-kMwMTN": "color-x1pzenjb",
    "outlineColor-kjBf7l": "outlineColor-x1pqgiyg",
    $$css: "wino-gc:src/resources/actions/Button.tsx:600"
  },
  disabled: {
    "backgroundColor-kWkggS": "backgroundColor-x6isuab",
    "borderColor-kVAM5u": "borderColor-x1gxkqr0",
    "color-kMwMTN": "color-xmx9m6c",
    $$css: "wino-gc:src/resources/actions/Button.tsx:606"
  }
};
// MARK: style:Success
const successSolidStyles = {
  default: {
    "backgroundColor-kWkggS": "backgroundColor-xbbp19g",
    "color-kMwMTN": "color-x1rfp1va",
    $$css: "wino-gc:src/resources/actions/Button.tsx:614"
  },
  hovered: {
    "backgroundColor-kWkggS": "backgroundColor-xmt2524",
    "color-kMwMTN": "color-x1rfp1va",
    "outlineColor-kjBf7l": "outlineColor-x1a1llv2",
    $$css: "wino-gc:src/resources/actions/Button.tsx:618"
  },
  pressed: {
    "backgroundColor-kWkggS": "backgroundColor-x1vfge6l",
    "color-kMwMTN": "color-x1rfp1va",
    "outlineColor-kjBf7l": "outlineColor-x1a1llv2",
    $$css: "wino-gc:src/resources/actions/Button.tsx:623"
  },
  disabled: {
    "backgroundColor-kWkggS": "backgroundColor-x1wt73u",
    "color-kMwMTN": "color-x1876a1j",
    $$css: "wino-gc:src/resources/actions/Button.tsx:628"
  }
};
const successGhostStyles = {
  default: {
    "backgroundColor-kWkggS": "backgroundColor-x14vcrn5",
    "borderColor-kVAM5u": "borderColor-x14mffum",
    "color-kMwMTN": "color-x1baitbu",
    $$css: "wino-gc:src/resources/actions/Button.tsx:634"
  },
  hovered: {
    "backgroundColor-kWkggS": "backgroundColor-x1fvp3id",
    "borderColor-kVAM5u": "borderColor-x14mffum",
    "color-kMwMTN": "color-x1ucu65l",
    "outlineColor-kjBf7l": "outlineColor-x1a1llv2",
    $$css: "wino-gc:src/resources/actions/Button.tsx:639"
  },
  pressed: {
    "backgroundColor-kWkggS": "backgroundColor-x14grjld",
    "borderColor-kVAM5u": "borderColor-x14mffum",
    "color-kMwMTN": "color-x1ucu65l",
    "outlineColor-kjBf7l": "outlineColor-x1a1llv2",
    $$css: "wino-gc:src/resources/actions/Button.tsx:645"
  },
  disabled: {
    "backgroundColor-kWkggS": "backgroundColor-x1wt73u",
    "borderColor-kVAM5u": "borderColor-xgv64iw",
    "color-kMwMTN": "color-x1876a1j",
    $$css: "wino-gc:src/resources/actions/Button.tsx:651"
  }
};
const successOutlineOrSubtleStyles = {
  default: {
    "backgroundColor-kWkggS": "backgroundColor-x6isuab",
    "borderColor-kVAM5u": "borderColor-xy1f2aw",
    "color-kMwMTN": "color-x1baitbu",
    $$css: "wino-gc:src/resources/actions/Button.tsx:658"
  },
  hovered: {
    "backgroundColor-kWkggS": "backgroundColor-x14vcrn5",
    "borderColor-kVAM5u": "borderColor-xf8yidl",
    "color-kMwMTN": "color-x1ucu65l",
    "outlineColor-kjBf7l": "outlineColor-x1a1llv2",
    $$css: "wino-gc:src/resources/actions/Button.tsx:663"
  },
  pressed: {
    "backgroundColor-kWkggS": "backgroundColor-x14grjld",
    "borderColor-kVAM5u": "borderColor-xf8yidl",
    "color-kMwMTN": "color-x1ucu65l",
    "outlineColor-kjBf7l": "outlineColor-x1a1llv2",
    $$css: "wino-gc:src/resources/actions/Button.tsx:669"
  },
  disabled: {
    "backgroundColor-kWkggS": "backgroundColor-x6isuab",
    "borderColor-kVAM5u": "borderColor-xgv64iw",
    "color-kMwMTN": "color-x1876a1j",
    $$css: "wino-gc:src/resources/actions/Button.tsx:675"
  }
};
// MARK: style:Danger
const dangerSolidStyles = {
  default: {
    "backgroundColor-kWkggS": "backgroundColor-x1ih3rzh",
    "color-kMwMTN": "color-x1rfp1va",
    $$css: "wino-gc:src/resources/actions/Button.tsx:683"
  },
  hovered: {
    "backgroundColor-kWkggS": "backgroundColor-xvy9btp",
    "color-kMwMTN": "color-x1rfp1va",
    "outlineColor-kjBf7l": "outlineColor-xgbbo6b",
    $$css: "wino-gc:src/resources/actions/Button.tsx:687"
  },
  pressed: {
    "backgroundColor-kWkggS": "backgroundColor-xl0augy",
    "color-kMwMTN": "color-x1rfp1va",
    "outlineColor-kjBf7l": "outlineColor-xgbbo6b",
    $$css: "wino-gc:src/resources/actions/Button.tsx:692"
  },
  disabled: {
    "backgroundColor-kWkggS": "backgroundColor-x1gn80mx",
    "color-kMwMTN": "color-x14jkkak",
    $$css: "wino-gc:src/resources/actions/Button.tsx:697"
  }
};
const dangerGhostStyles = {
  default: {
    "backgroundColor-kWkggS": "backgroundColor-x16nig27",
    "borderColor-kVAM5u": "borderColor-xvfuc4p",
    "color-kMwMTN": "color-x75p7gn",
    $$css: "wino-gc:src/resources/actions/Button.tsx:703"
  },
  hovered: {
    "backgroundColor-kWkggS": "backgroundColor-x6x82d",
    "borderColor-kVAM5u": "borderColor-xvfuc4p",
    "color-kMwMTN": "color-x1vryi6o",
    "outlineColor-kjBf7l": "outlineColor-xgbbo6b",
    $$css: "wino-gc:src/resources/actions/Button.tsx:708"
  },
  pressed: {
    "backgroundColor-kWkggS": "backgroundColor-xakmtdx",
    "borderColor-kVAM5u": "borderColor-xvfuc4p",
    "color-kMwMTN": "color-x1vryi6o",
    "outlineColor-kjBf7l": "outlineColor-xgbbo6b",
    $$css: "wino-gc:src/resources/actions/Button.tsx:714"
  },
  disabled: {
    "backgroundColor-kWkggS": "backgroundColor-x1gn80mx",
    "borderColor-kVAM5u": "borderColor-xe6rmvl",
    "color-kMwMTN": "color-x14jkkak",
    $$css: "wino-gc:src/resources/actions/Button.tsx:720"
  }
};
const dangerOutlineOrSubtleStyles = {
  default: {
    "backgroundColor-kWkggS": "backgroundColor-x6isuab",
    "borderColor-kVAM5u": "borderColor-x1t3gjgz",
    "color-kMwMTN": "color-x75p7gn",
    $$css: "wino-gc:src/resources/actions/Button.tsx:727"
  },
  hovered: {
    "backgroundColor-kWkggS": "backgroundColor-x16nig27",
    "borderColor-kVAM5u": "borderColor-xujbpp9",
    "outlineColor-kjBf7l": "outlineColor-xgbbo6b",
    "color-kMwMTN": "color-x1vryi6o",
    $$css: "wino-gc:src/resources/actions/Button.tsx:732"
  },
  pressed: {
    "backgroundColor-kWkggS": "backgroundColor-xakmtdx",
    "borderColor-kVAM5u": "borderColor-x1t3gjgz",
    "outlineColor-kjBf7l": "outlineColor-xgbbo6b",
    "color-kMwMTN": "color-x1vryi6o",
    $$css: "wino-gc:src/resources/actions/Button.tsx:738"
  },
  disabled: {
    "backgroundColor-kWkggS": "backgroundColor-x6isuab",
    "borderColor-kVAM5u": "borderColor-xe6rmvl",
    "color-kMwMTN": "color-x14jkkak",
    $$css: "wino-gc:src/resources/actions/Button.tsx:744"
  }
};
// MARK: style:Warning
const warningSolidStyles = {
  default: {
    "backgroundColor-kWkggS": "backgroundColor-x5ryxf7",
    "color-kMwMTN": "color-x1rfp1va",
    $$css: "wino-gc:src/resources/actions/Button.tsx:752"
  },
  hovered: {
    "backgroundColor-kWkggS": "backgroundColor-x4xk8rc",
    "color-kMwMTN": "color-x1rfp1va",
    "outlineColor-kjBf7l": "outlineColor-x14gn88t",
    $$css: "wino-gc:src/resources/actions/Button.tsx:756"
  },
  pressed: {
    "backgroundColor-kWkggS": "backgroundColor-xweyuun",
    "color-kMwMTN": "color-x1rfp1va",
    "outlineColor-kjBf7l": "outlineColor-x14gn88t",
    $$css: "wino-gc:src/resources/actions/Button.tsx:761"
  },
  disabled: {
    "backgroundColor-kWkggS": "backgroundColor-xlw2z58",
    "color-kMwMTN": "color-xbwtbte",
    $$css: "wino-gc:src/resources/actions/Button.tsx:766"
  }
};
const warningGhostStyles = {
  default: {
    "backgroundColor-kWkggS": "backgroundColor-x17ptzsk",
    "color-kMwMTN": "color-xhakqxv",
    $$css: "wino-gc:src/resources/actions/Button.tsx:772"
  },
  hovered: {
    "backgroundColor-kWkggS": "backgroundColor-xb7d9w3",
    "color-kMwMTN": "color-xqtwlnt",
    "outlineColor-kjBf7l": "outlineColor-x14gn88t",
    $$css: "wino-gc:src/resources/actions/Button.tsx:776"
  },
  pressed: {
    "backgroundColor-kWkggS": "backgroundColor-xl7xt31",
    "color-kMwMTN": "color-xqtwlnt",
    "outlineColor-kjBf7l": "outlineColor-x14gn88t",
    $$css: "wino-gc:src/resources/actions/Button.tsx:781"
  },
  disabled: {
    "backgroundColor-kWkggS": "backgroundColor-xlw2z58",
    "color-kMwMTN": "color-xbwtbte",
    $$css: "wino-gc:src/resources/actions/Button.tsx:786"
  }
};
const warningOutlineOrSubtleStyles = {
  default: {
    "backgroundColor-kWkggS": "backgroundColor-x6isuab",
    "borderColor-kVAM5u": "borderColor-x1v8w0bf",
    "color-kMwMTN": "color-xhakqxv",
    $$css: "wino-gc:src/resources/actions/Button.tsx:792"
  },
  hovered: {
    "backgroundColor-kWkggS": "backgroundColor-x17ptzsk",
    "borderColor-kVAM5u": "borderColor-x16fxru",
    "outlineColor-kjBf7l": "outlineColor-x14gn88t",
    "color-kMwMTN": "color-xqtwlnt",
    $$css: "wino-gc:src/resources/actions/Button.tsx:797"
  },
  pressed: {
    "backgroundColor-kWkggS": "backgroundColor-xl7xt31",
    "borderColor-kVAM5u": "borderColor-x16fxru",
    "outlineColor-kjBf7l": "outlineColor-x14gn88t",
    "color-kMwMTN": "color-xqtwlnt",
    $$css: "wino-gc:src/resources/actions/Button.tsx:803"
  },
  disabled: {
    "backgroundColor-kWkggS": "backgroundColor-x6isuab",
    "borderColor-kVAM5u": "borderColor-x1axnhfv",
    "color-kMwMTN": "color-xbwtbte",
    $$css: "wino-gc:src/resources/actions/Button.tsx:809"
  }
};
// MARK: style:Information
const informationSolidStyles = {
  default: {
    "backgroundColor-kWkggS": "backgroundColor-xod686e",
    "color-kMwMTN": "color-x1rfp1va",
    $$css: "wino-gc:src/resources/actions/Button.tsx:817"
  },
  hovered: {
    "backgroundColor-kWkggS": "backgroundColor-x1k3l3ya",
    "color-kMwMTN": "color-x1rfp1va",
    "outlineColor-kjBf7l": "outlineColor-x7kn4t5",
    $$css: "wino-gc:src/resources/actions/Button.tsx:821"
  },
  pressed: {
    "backgroundColor-kWkggS": "backgroundColor-x1yqugjk",
    "color-kMwMTN": "color-x1rfp1va",
    "outlineColor-kjBf7l": "outlineColor-x7kn4t5",
    $$css: "wino-gc:src/resources/actions/Button.tsx:826"
  },
  disabled: {
    "backgroundColor-kWkggS": "backgroundColor-x9w8woq",
    "color-kMwMTN": "color-xqyngj7",
    $$css: "wino-gc:src/resources/actions/Button.tsx:831"
  }
};
const informationGhostStyles = {
  default: {
    "backgroundColor-kWkggS": "backgroundColor-x1y40z5",
    "borderColor-kVAM5u": "borderColor-x1scfwcm",
    "color-kMwMTN": "color-xx67xh0",
    $$css: "wino-gc:src/resources/actions/Button.tsx:837"
  },
  hovered: {
    "backgroundColor-kWkggS": "backgroundColor-xuc3qs8",
    "borderColor-kVAM5u": "borderColor-x1scfwcm",
    "color-kMwMTN": "color-x1q21ffu",
    "outlineColor-kjBf7l": "outlineColor-x7kn4t5",
    $$css: "wino-gc:src/resources/actions/Button.tsx:842"
  },
  pressed: {
    "backgroundColor-kWkggS": "backgroundColor-x6ojpx5",
    "borderColor-kVAM5u": "borderColor-x1scfwcm",
    "color-kMwMTN": "color-x1q21ffu",
    "outlineColor-kjBf7l": "outlineColor-x7kn4t5",
    $$css: "wino-gc:src/resources/actions/Button.tsx:848"
  },
  disabled: {
    "backgroundColor-kWkggS": "backgroundColor-x9w8woq",
    "borderColor-kVAM5u": "borderColor-xmrkozs",
    "color-kMwMTN": "color-xqyngj7",
    $$css: "wino-gc:src/resources/actions/Button.tsx:854"
  }
};
const informationOutlineOrSubtleStyles = {
  default: {
    "backgroundColor-kWkggS": "backgroundColor-x6isuab",
    "borderColor-kVAM5u": "borderColor-x15ohlu4",
    "color-kMwMTN": "color-xx67xh0",
    $$css: "wino-gc:src/resources/actions/Button.tsx:861"
  },
  hovered: {
    "backgroundColor-kWkggS": "backgroundColor-x1y40z5",
    "borderColor-kVAM5u": "borderColor-x1hp5ggr",
    "color-kMwMTN": "color-x1q21ffu",
    "outlineColor-kjBf7l": "outlineColor-x7kn4t5",
    $$css: "wino-gc:src/resources/actions/Button.tsx:866"
  },
  pressed: {
    "backgroundColor-kWkggS": "backgroundColor-x6ojpx5",
    "borderColor-kVAM5u": "borderColor-x1hp5ggr",
    "color-kMwMTN": "color-x1q21ffu",
    "outlineColor-kjBf7l": "outlineColor-x7kn4t5",
    $$css: "wino-gc:src/resources/actions/Button.tsx:872"
  },
  disabled: {
    "backgroundColor-kWkggS": "backgroundColor-x6isuab",
    "borderColor-kVAM5u": "borderColor-xmrkozs",
    "color-kMwMTN": "color-xqyngj7",
    $$css: "wino-gc:src/resources/actions/Button.tsx:878"
  }
};
const stylesBySize = () => sizeStyles;
const stylesByState = (tone: Tone, variant: Variant) => {
  const withAccent = typeof tone === 'object' && tone.VAL.accent;
  switch (typeof tone === 'string' ? tone : tone.NAME) {
    case 'neutral':
      switch (variantName(variant)) {
        case 'solid':
          return neutralSolidStyles;
        case 'ghost':
          return withAccent ? neutralGhostWithAccentStyles : neutralGhostStyles;
        case 'outline':
        case 'subtle':
          return withAccent ? neutralOutlineOrSubtleWithAccentStyles : neutralOutlineOrSubtleStyles;
      }
    case 'brand':
      switch (variantName(variant)) {
        case 'solid':
          return brandSolidStyles;
        case 'ghost':
          return brandGhostStyles;
        case 'outline':
        case 'subtle':
          return brandOutlineOrSubtleStyles;
      }
    case 'success':
      switch (variantName(variant)) {
        case 'solid':
          return successSolidStyles;
        case 'ghost':
          return successGhostStyles;
        case 'outline':
        case 'subtle':
          return successOutlineOrSubtleStyles;
      }
    case 'danger':
      switch (variantName(variant)) {
        case 'solid':
          return dangerSolidStyles;
        case 'ghost':
          return dangerGhostStyles;
        case 'outline':
        case 'subtle':
          return dangerOutlineOrSubtleStyles;
      }
    case 'warning':
      switch (variantName(variant)) {
        case 'solid':
          return warningSolidStyles;
        case 'ghost':
          return warningGhostStyles;
        case 'outline':
        case 'subtle':
          return warningOutlineOrSubtleStyles;
      }
    case 'information':
      switch (variantName(variant)) {
        case 'solid':
          return informationSolidStyles;
        case 'ghost':
          return informationGhostStyles;
        case 'outline':
        case 'subtle':
          return informationOutlineOrSubtleStyles;
      }
  }
};

// NOTE - only: neutral tone with quiet & default state
const stylesQuietByVariant = (tone: Tone, state: State) => {
  const neutralTone = typeof tone === 'object' && tone.NAME === 'neutral';
  const withQuiet = typeof tone === 'object' && tone.VAL.quiet;
  const withAccent = typeof tone === 'object' && tone.VAL.accent;
  if (!neutralTone || !withQuiet || state !== 'default') return {};
  return withAccent ? neutralDefaultQuietWithAccentStyles : neutralDefaultQuietStyles;
};

// MARK: style:Icon
// ================
const iconColorFromParams = (tone: Tone, variant: Variant, disabled: boolean, hovered: boolean, pressed: boolean) => {
  if (variantName(variant) === 'solid') {
    if (disabled) {
      switch (typeof tone === 'string' ? tone : tone.NAME) {
        case 'neutral':
          return ColorNeutral.iconDisabled;
        case 'brand':
          return ColorBrand.iconDisabled;
        case 'success':
          return ColorSuccess.iconDisabled;
        case 'danger':
          return ColorDanger.iconDisabled;
        case 'warning':
          return ColorWarning.iconDisabled;
        case 'information':
          return ColorInformation.iconDisabled;
      }
    }
    return ColorNeutralInverse.iconAccent;
  }
  const withQuiet = typeof tone === 'object' && tone.VAL.quiet;
  switch (typeof tone === 'string' ? tone : tone.NAME) {
    case 'neutral':
      if (disabled) return ColorNeutral.iconDisabled;
      if (pressed || hovered) return ColorNeutral.iconAccentHover;
      return withQuiet ? ColorNeutral.iconDefault : ColorNeutral.iconAccent;
    case 'brand':
      if (disabled) return ColorBrand.iconDisabled;
      if (pressed || hovered) return ColorBrand.iconHover;
      return ColorBrand.iconDefault;
    case 'success':
      if (disabled) return ColorSuccess.iconDisabled;
      if (pressed || hovered) return ColorSuccess.iconHover;
      return ColorSuccess.iconDefault;
    case 'danger':
      if (disabled) return ColorDanger.iconDisabled;
      if (pressed || hovered) return ColorDanger.iconHover;
      return ColorDanger.iconDefault;
    case 'warning':
      if (disabled) return ColorWarning.iconDisabled;
      if (pressed || hovered) return ColorWarning.iconHover;
      return ColorWarning.iconDefault;
    case 'information':
      if (disabled) return ColorInformation.iconDisabled;
      if (pressed || hovered) return ColorInformation.iconHover;
      return ColorInformation.iconDefault;
  }
};
const iconDimensionsFromSize = (size: Size, variant: Variant, isIconButton: boolean) => {
  let baseSize = (() => {
    switch (size) {
      case 'small':
        return 12;
      case 'medium':
      case 'legacy_large':
      case 'large':
        return 16;
      case 'xlarge':
        return 20;
    }
  })();
  const upscaled = isIconButton && (size !== 'small' || variantName(variant) === 'subtle');
  const scale = upscaled ? ICONBUTTON_SCALE_RATIO : 1;
  return {
    width: baseSize * scale,
    height: baseSize * scale
  };
};
const styleProps = (disabled: boolean, pending: boolean, active: boolean, pressed: boolean, keyboardFocused: boolean, size: Size, shape: Shape, tone: Tone, bold: boolean, variant: Variant, grow: boolean, noShrink: boolean, isIconButton: boolean, appendStyle?: StyleXStyles) => {
  let state: State;
  switch (true) {
    case disabled:
      state = 'disabled';
      break;
    case pressed:
      state = 'pressed';
      break;
    case active:
      state = 'hovered';
      break;
    default:
      state = 'default';
      break;
  }
  let isPillShape: boolean;
  switch (true) {
    case shape === 'pill':
      isPillShape = true;
      break;
    case shape === 'rounded':
    default:
      isPillShape = false;
      break;
  }
  const ghostBordered = typeof variant === 'object' && variant.NAME === 'ghost' && variant.VAL.bordered === true;
  const isGhostVariant = variantName(variant) === 'ghost';
  return stylex.props(styles.root, grow && styles.root_grow, noShrink && styles.root_noShrink, (disabled || pending) && styles.root_disabled, keyboardFocused && styles.root_keyboardFocused, isPillShape && styles.root_pillShape, bold && (variantName(variant) === 'solid' && isWindowsOS ? styles.root_boldSolid_WindowsOS : styles.root_bold), variantName(variant) === 'outline' && styles.root_outline, variantName(variant) === 'subtle' && styles.root_subtle, (isIconButton && active && !isGhostVariant || ghostBordered) && styles.root_bordered, stylesBySize()[size], stylesByState(tone, variant)[state], (stylesQuietByVariant(tone, state) as any)[variantName(variant)], isIconButton && styles.root_iconButton, appendStyle);
};

// MARK: style:Spinner
// ===================
const spinnerBgColorFromParams = (tone: Tone, variant: Variant, hovered: boolean, pressed: boolean) => {
  const toneName = typeof tone === 'string' ? tone : tone.NAME;
  const withAccent = typeof tone === 'object' && tone.VAL.accent;
  if (pressed) {
    if (variantName(variant) === 'solid') {
      switch (toneName) {
        case 'neutral':
          return ColorNeutral.fillAccent;
        case 'brand':
          return ColorBrand.fillAccent;
        case 'success':
          return ColorSuccess.fillAccent;
        case 'danger':
          return ColorDanger.fillAccent;
        case 'warning':
          return ColorWarning.fillAccent;
        case 'information':
          return ColorInformation.fillAccent;
      }
    }
    if (variantName(variant) === 'ghost') {
      switch (toneName) {
        case 'neutral':
          return ColorNeutral.fillPressed;
        case 'brand':
          return ColorBrand.fillPressed;
        case 'success':
          return ColorSuccess.fillPressed;
        case 'danger':
          return ColorDanger.fillPressed;
        case 'warning':
          return ColorWarning.fillPressed;
        case 'information':
          return ColorInformation.fillPressed;
      }
    }
    switch (toneName) {
      case 'neutral':
        return ColorNeutral.fillFadedPressed;
      case 'brand':
        return ColorBrand.fillPressed;
      case 'success':
        return ColorSuccess.fillPressed;
      case 'danger':
        return ColorDanger.fillPressed;
      case 'warning':
        return ColorWarning.fillPressed;
      case 'information':
        return ColorInformation.fillPressed;
    }
  }
  if (hovered) {
    if (variantName(variant) === 'solid') {
      switch (toneName) {
        case 'neutral':
          return ColorNeutral.fillAccentHover;
        case 'brand':
          return ColorBrand.fillHover;
        case 'success':
          return ColorSuccess.fillHover;
        case 'danger':
          return ColorDanger.fillHover;
        case 'warning':
          return ColorWarning.fillHover;
        case 'information':
          return ColorInformation.fillHover;
      }
    }
    if (variantName(variant) === 'ghost') {
      switch (toneName) {
        case 'neutral':
          return withAccent ? ColorNeutral.fillHover : ColorNeutral.fillFadedHover;
        case 'brand':
          return ColorBrand.fillWeakHover;
        case 'success':
          return ColorSuccess.fillWeakHover;
        case 'danger':
          return ColorDanger.fillWeakHover;
        case 'warning':
          return ColorWarning.fillWeakHover;
        case 'information':
          return ColorInformation.fillWeakHover;
      }
    }
    switch (toneName) {
      case 'neutral':
        return withAccent ? ColorNeutral.fillDefault : ColorNeutral.fillFaded;
      case 'brand':
        return ColorBrand.fillWeak;
      case 'success':
        return ColorSuccess.fillWeak;
      case 'danger':
        return ColorDanger.fillWeak;
      case 'warning':
        return ColorWarning.fillWeak;
      case 'information':
        return ColorInformation.fillWeak;
    }
  }
  if (variantName(variant) === 'solid') {
    switch (toneName) {
      case 'neutral':
        return ColorNeutral.fillAccent;
      case 'brand':
        return ColorBrand.fillDefault;
      case 'success':
        return ColorSuccess.fillDefault;
      case 'danger':
        return ColorDanger.fillDefault;
      case 'warning':
        return ColorWarning.fillDefault;
      case 'information':
        return ColorInformation.fillDefault;
    }
  }
  if (variantName(variant) === 'ghost') {
    switch (toneName) {
      case 'neutral':
        return withAccent ? ColorNeutral.fillDefault : ColorNeutral.fillFaded;
      case 'brand':
        return ColorBrand.fillWeak;
      case 'success':
        return ColorSuccess.fillWeak;
      case 'danger':
        return ColorDanger.fillWeak;
      case 'warning':
        return ColorWarning.fillWeak;
      case 'information':
        return ColorInformation.fillWeak;
    }
  }
  if (variantName(variant) === 'outline') return 'white';
  return 'transparent'; // variant 'subtle'
};
const spinnerProps = (pending: boolean, isIconButton: boolean, width: number, tone: Tone, variant: Variant, active: boolean, pressed: boolean) => stylex.props(styles.spinner_root, isIconButton && styles.spinner_root_iconButton, styles.spinner_rootFromProps(pending, isIconButton, width, spinnerBgColorFromParams(tone, variant, active, pressed)));
const iconWrapperStyleProps = () => ({
  className: "position-x1n2onr6 display-x3nfvp2",
  "data-style-src": "wino-gc:src/resources/actions/Button.tsx:1396"
});
const labelStyleProps = (pressed: boolean) => ({
  0: {
    className: "display-x3nfvp2 transition-x1hgg8ub minWidth-xk8lq53",
    "data-style-src": "wino-gc:src/resources/actions/Button.tsx:1400"
  },
  1: {
    className: "display-x3nfvp2 minWidth-xk8lq53 willChange-x1so62im transform-xfimr68 transition-xq2gx43",
    "data-style-src": "wino-gc:src/resources/actions/Button.tsx:1400; wino-gc:src/resources/actions/Button.tsx:1405"
  }
})[!!pressed << 0];
const statusLightStyleProps = (iconSize: number, animated: boolean) => {
  const size = iconSize * STATUSLIGHT_RATIO;
  return stylex.props(styles.statusLight_root, animated && styles.statusLight_root_animated, styles.statusLight_rootFromProps(size));
};

// MARK: style
// ===========
const ICONBUTTON_SCALE_RATIO = 1.2;
const STATUSLIGHT_RATIO = 0.4;
const statusLightAnimation = "x11dwn1d-B";
const _temp = {
  "opacity-kSiTet": "opacity-xb4nw82",
  "transitionProperty-k1ekBW": "transitionProperty-x1cmnkkz",
  "boxShadow-kGVxlE": "boxShadow-x1iotiob",
  "$$css": "wino-gc:src/resources/actions/Button.tsx:1384"
};
const styles = {
  root: {
    "position-kVAEAm": "position-x1n2onr6",
    "display-k1xSpc": "display-x3nfvp2",
    "alignItems-kGNEyG": "alignItems-x6s0dn4",
    "justifyContent-kjj79g": "justifyContent-xl56j7k",
    "overflow-kVQacm": "overflow-xb3r6kr",
    "borderRadius-kaIpWk": "borderRadius-x1qd5jr3",
    "boxSizing-kB7OPa": "boxSizing-x9f619",
    "fontFamily-kMv6JI": "fontFamily-x111x8ze",
    "fontWeight-k63SB2": "fontWeight-x1fkahbb",
    "fontSize-kGuDYH": "fontSize-x106xqen",
    "textAlign-k9WMMc": "textAlign-x2b8uid",
    "cursor-kkrTdU": "cursor-x1ypdohk",
    "transition-kmkexE": "transition-x1hgg8ub",
    "borderWidth-kMzoRj": "borderWidth-xc342km",
    "outline-kI3sdo": "outline-x1a2a7pz",
    $$css: "wino-gc:src/resources/actions/Button.tsx:1306"
  },
  root_bold: {
    "fontWeight-k63SB2": "fontWeight-x5x31i3",
    $$css: "wino-gc:src/resources/actions/Button.tsx:1323"
  },
  root_boldSolid_WindowsOS: {
    "fontWeight-k63SB2": "fontWeight-x4f3304",
    "letterSpacing-kb6lSQ": "letterSpacing-xcheg63",
    $$css: "wino-gc:src/resources/actions/Button.tsx:1327"
  },
  root_outline: {
    "--bgColor_filledOrTransparent-x1khriwu": "--bgColor_filledOrTransparent-x1khriwu-x1cnd0s7",
    "borderWidth-kMzoRj": "borderWidth-xmkeg23",
    "borderStyle-ksu8eU": "borderStyle-x1y0btm7",
    $$css: "wino-gc:src/resources/actions/Button.tsx:1331"
  },
  root_subtle: {
    "--bgColor_filledOrTransparent-x1khriwu": "--bgColor_filledOrTransparent-x1khriwu-x1kgshrk",
    $$css: "wino-gc:src/resources/actions/Button.tsx:1336"
  },
  root_grow: {
    "flex-kUk6DE": "flex-x98rzlu",
    $$css: "wino-gc:src/resources/actions/Button.tsx:1339"
  },
  root_noShrink: {
    "flexShrink-kmuXW": "flexShrink-x2lah0s",
    "whiteSpace-khDVqt": "whiteSpace-xuxw1ft",
    $$css: "wino-gc:src/resources/actions/Button.tsx:1342"
  },
  root_bordered: {
    "borderWidth-kMzoRj": "borderWidth-xmkeg23",
    "borderStyle-ksu8eU": "borderStyle-x1y0btm7",
    $$css: "wino-gc:src/resources/actions/Button.tsx:1346"
  },
  root_disabled: {
    "cursor-kkrTdU": "cursor-x1h6gzvc",
    $$css: "wino-gc:src/resources/actions/Button.tsx:1350"
  },
  root_keyboardFocused: {
    "outlineStyle-k3XXqK": "outlineStyle-xaatb59",
    "outlineWidth-kMeerF": "outlineWidth-x1de99jn",
    "outlineOffset-kInvED": "outlineOffset-x1y3gkto",
    "transitionProperty-k1ekBW": "transitionProperty-x13b0p5u",
    $$css: "wino-gc:src/resources/actions/Button.tsx:1353"
  },
  root_pillShape: {
    "borderRadius-kaIpWk": "borderRadius-x1vk07mu",
    $$css: "wino-gc:src/resources/actions/Button.tsx:1359"
  },
  root_iconButton: {
    "minWidth-k7Eaqz": "minWidth-x1qg10q6",
    "width-kzqmXN": "width-xxpy1pu",
    "minHeight-kAzted": "minHeight-xcz8e9z",
    "height-kZKoxP": "height-x1iq24yq",
    $$css: "wino-gc:src/resources/actions/Button.tsx:1362"
  },
  spinner_root: {
    "position-kVAEAm": "position-x10l6tqk",
    "right-kCIrl2": "right-x1tf4rqa",
    "overflow-kVQacm": "overflow-xb3r6kr",
    "alignSelf-kSGwAc": "alignSelf-xamitd3",
    "display-k1xSpc": "display-x78zum5",
    "alignItems-kGNEyG": "alignItems-x6s0dn4",
    "justifyContent-kjj79g": "justifyContent-xl56j7k",
    "transition-kmkexE": "transition-x1hgg8ub",
    "transitionDuration-kIyJzY": "transitionDuration-x1rvut6x",
    $$css: "wino-gc:src/resources/actions/Button.tsx:1368"
  },
  spinner_root_iconButton: {
    "right-kCIrl2": "right-xwukr4l",
    "left-kbCHJM": "left-x1nrll8i",
    "transform-k3aq6I": "transform-xuuh30",
    $$css: "wino-gc:src/resources/actions/Button.tsx:1379"
  },
  spinner_rootFromProps: (pending: boolean, isIconButton: boolean, width: number, bgColor: string) => [_temp, {
    "width-kzqmXN": (pending ? width : 0) != null ? "width-x5lhr3w" : pending ? width : 0,
    "backgroundColor-kWkggS": bgColor != null ? "backgroundColor-xl8spv7" : bgColor,
    $$css: "wino-gc:src/resources/actions/Button.tsx:1384"
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(pending ? width : 0),
    "--x-opacity": (pending ? 1 : 0) != null ? pending ? 1 : 0 : undefined,
    "--x-transitionProperty": (isIconButton ? 'opacity' : 'all') != null ? isIconButton ? 'opacity' : 'all' : undefined,
    "--x-boxShadow": `${bgColor} 0 0 12px 13px` != null ? `${bgColor} 0 0 12px 13px` : undefined,
    "--x-backgroundColor": bgColor != null ? bgColor : undefined
  }],
  statusLight_root: {
    "position-kVAEAm": "position-x10l6tqk",
    "backgroundColor-kWkggS": "backgroundColor-x1ryq1pb",
    "borderRadius-kaIpWk": "borderRadius-xj42ba9",
    "borderStyle-ksu8eU": "borderStyle-x1y0btm7",
    "borderWidth-kMzoRj": "borderWidth-xvndefy",
    "borderColor-kVAM5u": "borderColor-x1nvupfo",
    "boxSizing-kB7OPa": "boxSizing-x1afcbsf",
    "right-kCIrl2": "right-x1bkpikb",
    "top-k87sOh": "top-x1fo6t33",
    "::after_content-k5JduY": "content-x1s928wv",
    "::after_position-kwXMNM": "position-x1j6awrg",
    "::after_top-k3foIR": "top-x1m1drc7",
    "::after_left-kH8cDV": "left-x17cx49",
    "::after_width-kkgrvl": "width-x4eaejv",
    "::after_height-k4zj60": "height-x1wsn0xg",
    "::after_borderRadius-kUfP38": "borderRadius-xjrxidl",
    "::after_backgroundColor-ks3ayO": "backgroundColor-x4lo36m",
    "::after_opacity-kypkao": "opacity-x1w3onc2",
    $$css: "wino-gc:src/resources/actions/Button.tsx:1410"
  },
  statusLight_root_animated: {
    "::after_animationName-kFguJn": "animationName-x1jn8n9c",
    "::after_animationTimingFunction-kYqdJW": "animationTimingFunction-x152856",
    "::after_animationDuration-kh3gPk": "animationDuration-x1ui9q4k",
    "::after_animationIterationCount-kVUETb": "animationIterationCount-x141gxe9",
    $$css: "wino-gc:src/resources/actions/Button.tsx:1432"
  },
  statusLight_rootFromProps: (size: number) => [{
    "width-kzqmXN": size != null ? "width-x5lhr3w" : size,
    "height-kZKoxP": size != null ? "height-x16ye13r" : size,
    $$css: "wino-gc:src/resources/actions/Button.tsx:1440"
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(size),
    "--x-height": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(size)
  }]
};